import { LegalFooter, setLegalConfig, type LegalConfig } from "../legal.js";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { SessionProvider, type Session } from "../api/session.js";
import { AuthError, AuthManager, type TokenSource } from "./auth.js";
import { CognitoPasswordAuth } from "./cognito-password.js";
import { AuthScreens } from "./AuthScreens.js";
import { isUnconfigured } from "../runtime-config.js";
import type { OidcConfig } from "./pkce.js";

/**
 * Authentication gate. States:
 *   loading → (callback handling) → signed-out / sign-in form → tenant discovery →
 *   no-membership | tenant picker | app
 * Runtime configuration comes from /config.json (written at deploy time) so the
 * same build runs in every environment. Two sign-in modes are supported:
 *   - "oidc": Cognito Hosted UI (Authorization Code + PKCE, full-page redirect).
 *   - "cognito-password": in-house email/password form (USER_PASSWORD_AUTH),
 *     no Hosted UI and no redirect.
 * A static token from window.__COREFINOPS__ is honoured ONLY as local/test mode.
 */

type PasswordAuthConfig = { readonly mode: "cognito-password"; readonly clientId: string; readonly region?: string; readonly idpUrl: string };

export interface RuntimeConfig {
  readonly apiBaseUrl: string;
  readonly auth: ({ readonly mode: "oidc" } & OidcConfig) | PasswordAuthConfig | { readonly mode: "static-token" };
  readonly environment?: string;
  /** Base URL of the isolated admin API (for self-service access requests). */
  readonly adminApiBaseUrl?: string;
  /** B10: owner-supplied legal and support links. */
  readonly legal?: LegalConfig["legal"];
  readonly support?: LegalConfig["support"];
}

interface Tenant {
  tenantId: string;
  name: string;
  roles: string[];
}

type State =
  | { kind: "loading"; message: string }
  | { kind: "error"; message: string; retry?: () => void }
  | { kind: "unsubscribe"; token: string; apiBaseUrl: string }
  | { kind: "signed-out"; reason?: string }
  | { kind: "auth"; reason?: string }
  | { kind: "no-membership"; sub: string }
  | { kind: "marketplace"; tenants: Tenant[]; handle: string }
  | { kind: "pick-tenant"; tenants: Tenant[] }
  | { kind: "ready"; session: Session };

declare global {
  interface Window {
    __COREFINOPS__?: Partial<Session> & { localTestMode?: boolean };
  }
}

const TENANT_KEY = "corefinops.activeTenant";
/** MP-02: the opaque Marketplace registration handle, held only for this tab. */
const MP_HANDLE_KEY = "corefinops.marketplaceHandle";

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * The AWS Marketplace fulfilment endpoint redirects to /marketplace/register
 * with `#handle=…` (or `#error=…`) in the FRAGMENT. Move the handle into this
 * tab's session storage and strip it from the address bar so it never lands in
 * history, bookmarks or analytics. Returns an error code if AWS sign-up failed.
 */
function captureMarketplaceLanding(): string | null {
  if (window.location.pathname !== "/marketplace/register") return null;
  const frag = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const handle = frag.get("handle");
  if (handle) storage()?.setItem(MP_HANDLE_KEY, handle);
  window.history.replaceState(null, "", "/marketplace/register");
  return frag.get("error");
}

const MP_ERRORS: Record<string, string> = {
  missing_token: "AWS Marketplace did not send a registration token. Return to AWS Marketplace and choose \"Set up your account\" again.",
  resolve_failed: "We could not confirm your AWS Marketplace subscription. Please try \"Set up your account\" again in a few minutes.",
  invalid_request: "The AWS Marketplace sign-up request was invalid.",
  token_expired: "Your AWS Marketplace sign-up link has expired or was already used. Return to AWS Marketplace and choose \"Set up your account\" again.",
  temporarily_unavailable: "AWS Marketplace could not be reached just now. Please return to AWS Marketplace and choose \"Set up your account\" again in a few minutes.",
  wrong_product: "This AWS Marketplace subscription is not for CoreFinOps.",
  rate_limited: "Too many sign-up attempts. Please wait a minute, then choose \"Set up your account\" in AWS Marketplace again.",
};

async function loadConfig(): Promise<RuntimeConfig> {
  const res = await fetch("/config.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`Configuration unavailable (${res.status}).`);
  const cfg = (await res.json()) as RuntimeConfig;
  // REL-02 fail-closed: reject a config that was never populated for the target
  // environment (the build emits an explicit "unconfigured" sentinel rather than
  // silently defaulting to production). This surfaces a clear operator error
  // instead of the SPA quietly calling the wrong environment's API/Cognito.
  if (isUnconfigured(cfg)) {
    throw new Error("This deployment is not configured. Set RUNTIME_CONFIG (or deploy config.json) for this environment before use.");
  }
  // Transport check: production origins must be https; localhost/relative are
  // allowed for local dev and same-origin deploys. (The build-time validator
  // separately requires https for operator-supplied RUNTIME_CONFIG.)
  const isLocalHost = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(cfg.apiBaseUrl);
  if (!/^https:\/\//.test(cfg.apiBaseUrl) && !cfg.apiBaseUrl.startsWith("/") && !isLocalHost) {
    throw new Error("Invalid runtime configuration: apiBaseUrl must be an https:// URL.");
  }
  // The OIDC redirect/logout targets must match the origin that actually served
  // the SPA (CloudFront, Vercel, a preview URL, localhost…). Deriving them from
  // the live origin instead of a build-time constant lets one build run on any
  // host. The origin still has to be registered in the Cognito app client.
  if (cfg.auth.mode === "oidc") {
    const origin = window.location.origin;
    return { ...cfg, auth: { ...cfg.auth, redirectUri: `${origin}/auth/callback`, logoutRedirectUri: `${origin}/` } };
  }
  return cfg;
}

function browserAuth(cfg: RuntimeConfig & { auth: { mode: "oidc" } & OidcConfig }): AuthManager {
  return new AuthManager({
    config: cfg.auth,
    storage: window.sessionStorage,
    fetch: window.fetch.bind(window),
    crypto: window.crypto,
    now: () => Date.now(),
    redirect: (u) => window.location.assign(u),
    replaceUrl: (u) => window.history.replaceState(null, "", u),
  });
}

function browserPasswordAuth(cfg: RuntimeConfig & { auth: PasswordAuthConfig }): CognitoPasswordAuth {
  return new CognitoPasswordAuth({
    config: { clientId: cfg.auth.clientId, idpUrl: cfg.auth.idpUrl },
    storage: window.sessionStorage,
    fetch: window.fetch.bind(window),
    now: () => Date.now(),
  });
}

function UnsubscribePage({ token, apiBaseUrl }: { token: string; apiBaseUrl: string }): ReactNode {
  const [phase, setPhase] = useState<"ready" | "working" | "done" | "failed">(token ? "ready" : "failed");
  const go = async (): Promise<void> => {
    setPhase("working");
    try {
      const res = await fetch(`${apiBaseUrl.replace(/\/$/, "")}/email/unsubscribe`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token }) });
      setPhase(res.ok ? "done" : "failed");
    } catch {
      setPhase("failed");
    }
  };
  return (
    <Centered>
      <h1 style={{ fontSize: "1.3rem" }}>Stop CoreFinOps alert emails</h1>
      {phase === "ready" || phase === "working" ? (
        <>
          <p>This stops alert emails to you from this workspace. Other people are not affected, and you can turn them back on in Plan &amp; billing.</p>
          <button type="button" onClick={() => void go()} disabled={phase === "working"}>{phase === "working" ? "Working…" : "Stop alert emails"}</button>
        </>
      ) : null}
      {phase === "done" ? <p role="status">Done. You will not get alert emails from this workspace any more.</p> : null}
      {phase === "failed" ? <p role="alert" style={{ color: "#a11" }}>This link is invalid or has expired. Sign in and use Plan &amp; billing &rarr; Alerts to change your email settings.</p> : null}
    </Centered>
  );
}

function Centered({ children }: { children: ReactNode }): ReactNode {
  return (
    <div style={{ fontFamily: "system-ui, sans-serif", maxWidth: 440, margin: "12vh auto", padding: "0 1rem", color: "#1a1a1a" }}>
      {children}
      <LegalFooter />
    </div>
  );
}

export function AuthGate({ children }: { children: ReactNode }): ReactNode {
  const [state, setState] = useState<State>({ kind: "loading", message: "Loading…" });
  const [cfg, setCfg] = useState<RuntimeConfig | null>(null);
  const [tenantCount, setTenantCount] = useState(0);
  const qc = useQueryClient();
  const auth = useMemo(() => (cfg && cfg.auth.mode === "oidc" ? browserAuth(cfg as RuntimeConfig & { auth: { mode: "oidc" } & OidcConfig }) : null), [cfg]);
  const passwordAuth = useMemo(() => (cfg && cfg.auth.mode === "cognito-password" ? browserPasswordAuth(cfg as RuntimeConfig & { auth: PasswordAuthConfig }) : null), [cfg]);

  const discoverTenants = useCallback(
    async (c: RuntimeConfig, a: TokenSource, switching = false) => {
      setState({ kind: "loading", message: "Loading your workspaces…" });
      const token = await a.currentToken();
      if (!token) return setState(signedOutState(c, "Your session has expired. Please sign in again."));
      const res = await fetch(`${c.apiBaseUrl.replace(/\/$/, "")}/me/tenants`, { headers: { authorization: `Bearer ${token}`, accept: "application/json" } });
      if (res.status === 401) {
        a.clear();
        return setState(signedOutState(c, "Your session is no longer valid. Please sign in again."));
      }
      if (!res.ok) return setState({ kind: "error", message: `Could not load your workspaces (${res.status}).`, retry: () => void discoverTenants(c, a) });
      const body = (await res.json()) as { userSub: string; tenants: Tenant[] };
      // A pending AWS Marketplace purchase is linked before anything else.
      const mpHandle = storage()?.getItem(MP_HANDLE_KEY);
      if (mpHandle) return setState({ kind: "marketplace", tenants: body.tenants, handle: mpHandle });
      if (body.tenants.length === 0) return setState({ kind: "no-membership", sub: body.userSub });
      setTenantCount(body.tenants.length);
      const remembered = window.sessionStorage.getItem(TENANT_KEY);
      // When explicitly switching, always show the picker so the user sees their
      // workspaces. On initial load, auto-select if there is only one or a remembered one.
      const chosen = !switching && (body.tenants.length === 1 ? body.tenants[0] : body.tenants.find((t) => t.tenantId === remembered));
      if (!chosen) return setState({ kind: "pick-tenant", tenants: body.tenants });
      const s = a.session()!;
      // Land on the dashboard: at the bare root (e.g. after password sign-in),
      // make the URL explicitly /overview.
      if (window.location.pathname === "/" || window.location.pathname === "") window.history.replaceState(null, "", "/overview");
      setState({
        kind: "ready",
        session: { userSub: body.userSub, email: s.email, tenantId: chosen.tenantId, tenantName: chosen.name, roles: chosen.roles, token: () => a.currentToken(), apiBaseUrl: c.apiBaseUrl },
      });
    },
    []
  );

  useEffect(() => {
    void (async () => {
      // Explicit local/test mode only (never used by a deployed build).
      const injected = window.__COREFINOPS__;
      if (injected?.localTestMode && injected.token && injected.tenantId) {
        setState({ kind: "ready", session: { userSub: injected.userSub ?? "local", tenantId: injected.tenantId, token: injected.token, apiBaseUrl: injected.apiBaseUrl ?? "/api" } });
        return;
      }
      const mpError = captureMarketplaceLanding();
      if (mpError) {
        return setState({ kind: "error", message: MP_ERRORS[mpError] ?? "AWS Marketplace sign-up failed.", retry: () => window.location.assign("/") });
      }
      let c: RuntimeConfig;
      try {
        c = await loadConfig();
      } catch (e) {
        return setState({ kind: "error", message: (e as Error).message, retry: () => window.location.reload() });
      }
      setCfg(c);
      setLegalConfig(c);

      // B11: the unsubscribe link from an alert email. Public: no sign-in. The signed token is in the
      // URL FRAGMENT (never sent to a server or written to access logs) and is removed from the address bar.
      if (window.location.pathname === "/unsubscribe") {
        const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") ?? "";
        window.history.replaceState(null, "", "/unsubscribe");
        return setState({ kind: "unsubscribe", token, apiBaseUrl: c.apiBaseUrl });
      }

      if (c.auth.mode === "cognito-password") {
        const a = browserPasswordAuth(c as RuntimeConfig & { auth: PasswordAuthConfig });
        if (!a.session()) return setState({ kind: "auth" });
        return void discoverTenants(c, a);
      }

      if (c.auth.mode !== "oidc") return setState({ kind: "error", message: "This deployment has no sign-in configured." });
      const a = browserAuth(c as RuntimeConfig & { auth: { mode: "oidc" } & OidcConfig });
      if (window.location.pathname === "/auth/callback") {
        setState({ kind: "loading", message: "Completing sign-in…" });
        try {
          const back = await a.completeSignIn(window.location);
          window.location.hash = back.slice(1);
        } catch (e) {
          return setState({ kind: "signed-out", reason: e instanceof AuthError ? e.message : "Sign-in failed. Please try again." });
        }
      }
      if (!a.session()) return setState({ kind: "signed-out" });
      await discoverTenants(c, a);
    })();
  }, [discoverTenants]);

  const onUnauthorized = useCallback(() => {
    auth?.clear();
    passwordAuth?.clear();
    qc.clear();
    setState(cfg ? signedOutState(cfg, "Your session has expired. Please sign in again.") : { kind: "signed-out", reason: "Your session has expired. Please sign in again." });
  }, [auth, passwordAuth, cfg, qc]);

  const signOut = useCallback(() => {
    qc.clear();
    window.sessionStorage.removeItem(TENANT_KEY);
    if (auth) return auth.signOut();
    passwordAuth?.clear();
    setState({ kind: "auth" });
  }, [auth, passwordAuth, qc]);

  const switchTenant = useCallback(() => {
    window.sessionStorage.removeItem(TENANT_KEY);
    qc.clear();
    const active = auth ?? passwordAuth;
    if (cfg && active) void discoverTenants(cfg, active, true).then(() => undefined);
  }, [auth, passwordAuth, cfg, discoverTenants, qc]);

  // Only expose switchTenant when there are multiple workspaces to choose from.
  const sessionControls = auth || passwordAuth ? { signOut, ...(tenantCount > 1 ? { switchTenant } : {}) } : {};

  switch (state.kind) {
    case "loading":
      return <Centered><p role="status">{state.message}</p></Centered>;
    case "error":
      return (
        <Centered>
          <h1 style={{ fontSize: "1.3rem" }}>CoreFinOps</h1>
          <p role="alert" style={{ color: "#a11" }}>{state.message}</p>
          {state.retry ? <button type="button" onClick={state.retry}>Try again</button> : null}
        </Centered>
      );
    case "auth":
      return passwordAuth && cfg ? (
        <AuthScreens
          auth={passwordAuth}
          onAuthenticated={() => void discoverTenants(cfg, passwordAuth)}
          isLocal={cfg.environment === "local"}
          {...(state.reason ? { reason: state.reason } : {})}
        />
      ) : (
        <Centered><p role="status">Loading…</p></Centered>
      );
    case "unsubscribe":
      return <UnsubscribePage token={state.token} apiBaseUrl={state.apiBaseUrl} />;
    case "signed-out":
      return (
        <Centered>
          <h1 style={{ fontSize: "1.5rem", marginBottom: 4 }}>CoreFinOps</h1>
          <p style={{ color: "#555", marginTop: 0 }}>Know the cost before you move. Control it while you move. Prove the value after.</p>
          {state.reason ? <p role="alert" style={{ color: "#8a5a00", background: "#fff7e0", padding: "0.5rem 0.75rem", borderRadius: 6 }}>{state.reason}</p> : null}
          <button type="button" onClick={() => void auth?.signIn(window.location.hash || "#/overview")} disabled={!auth} style={{ padding: "0.6rem 1.2rem", fontSize: "1rem", background: "#1d6fb8", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
            Sign in
          </button>
          <p style={{ color: "#777", fontSize: "0.8rem", marginTop: 24 }}>You will be redirected to your organization&apos;s CoreFinOps sign-in page.</p>
        </Centered>
      );
    case "no-membership":
      return (
        <NoMembership
          sub={state.sub}
          apiBaseUrl={cfg?.apiBaseUrl}
          adminApiBaseUrl={cfg?.adminApiBaseUrl}
          tokenSource={auth ?? passwordAuth}
          onSignOut={signOut}
          onWorkspace={(tenantId) => {
            storage()?.setItem(TENANT_KEY, tenantId);
            const active = auth ?? passwordAuth;
            if (cfg && active) void discoverTenants(cfg, active);
          }}
        />
      );
    case "marketplace":
      return (
        <MarketplaceLink
          handle={state.handle}
          tenants={state.tenants}
          apiBaseUrl={cfg?.apiBaseUrl ?? ""}
          tokenSource={auth ?? passwordAuth}
          onSignOut={signOut}
          onDone={(tenantId) => {
            storage()?.removeItem(MP_HANDLE_KEY);
            if (tenantId) storage()?.setItem(TENANT_KEY, tenantId);
            window.history.replaceState(null, "", "/overview");
            const active = auth ?? passwordAuth;
            if (cfg && active) void discoverTenants(cfg, active);
          }}
        />
      );
    case "pick-tenant":
      return (
        <Centered>
          <h1 style={{ fontSize: "1.3rem" }}>Choose a workspace</h1>
          <ul style={{ listStyle: "none", padding: 0 }}>
            {state.tenants.map((t) => (
              <li key={t.tenantId} style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    window.sessionStorage.setItem(TENANT_KEY, t.tenantId);
                    const active = auth ?? passwordAuth;
                    if (cfg && active) void discoverTenants(cfg, active);
                  }}
                >
                  {t.name} <span style={{ color: "#777" }}>({t.roles.join(", ")})</span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={signOut}>Sign out</button>
        </Centered>
      );
    case "ready":
      return (
        <SessionProvider session={state.session} onUnauthorized={onUnauthorized} {...sessionControls}>
          {children}
        </SessionProvider>
      );
  }
}

/** The right signed-out state for the configured mode, carrying an optional reason. */
function signedOutState(cfg: RuntimeConfig, reason: string): State {
  return cfg.auth.mode === "cognito-password" ? { kind: "auth", reason } : { kind: "signed-out", reason };
}

/**
 * Shown when a verified user belongs to no tenant. Offers a self-service
 * "Request access" that posts to the isolated admin API; a tenant org_admin then
 * approves it from /admin/dashboard. Falls back to the plain "ask your admin"
 * message if no admin API is configured for this deployment.
 */
let capturedInvite: string | null = null;

/**
 * An admin-issued invitation link carries its one-time code in the URL FRAGMENT
 * (#invite=cfi-...), which browsers never send to a server. Read it once, then
 * remove it from the address bar and history entry so it is not left on screen.
 */
export function captureInviteFragment(): string {
  if (capturedInvite !== null) return capturedInvite;
  const raw = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("invite") ?? "";
  capturedInvite = /^cfi-[A-Za-z0-9_-]{20,80}$/.test(raw) ? raw : "";
  if (raw) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return capturedInvite;
}

function NoMembership({
  sub,
  apiBaseUrl,
  adminApiBaseUrl,
  tokenSource,
  onSignOut,
  onWorkspace,
}: {
  sub: string;
  apiBaseUrl: string | undefined;
  adminApiBaseUrl: string | undefined;
  tokenSource: TokenSource | null;
  onSignOut: () => void;
  onWorkspace: (tenantId: string) => void;
}): ReactNode {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "joined" | "already" | "error">("idle");
  const [message, setMessage] = useState("");
  // SEC-01: a request must name an authorized target. An invitation token is
  // the primary path; an organization request URL can pre-fill ?inviteTenant=…
  // (explicit tenant id, never inferred from an email domain).
  const [inviteToken, setInviteToken] = useState(captureInviteFragment);
  const orgTenantId = useMemo(() => new URLSearchParams(window.location.search).get("inviteTenant") ?? "", []);
  const [err, setErr] = useState<string | undefined>();

  const request = async () => {
    if (!adminApiBaseUrl || !tokenSource) return;
    const token = inviteToken.trim();
    if (!token && !orgTenantId) {
      setErr("Enter the invitation code your administrator sent you.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErr(undefined);
    try {
      const bearer = await tokenSource.currentToken();
      if (!bearer) throw new Error("Your session has expired. Please sign in again.");
      const res = await fetch(`${adminApiBaseUrl.replace(/\/$/, "")}/access-requests`, {
        method: "POST",
        headers: { authorization: `Bearer ${bearer}`, "content-type": "application/json" },
        body: JSON.stringify({
          message: message.trim() || undefined,
          ...(token ? { token } : {}),
          ...(!token && orgTenantId ? { tenantId: orgTenantId } : {}),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { status?: string; error?: string };
      if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status}).`);
      setStatus(data.status === "already_member" ? "already" : data.status === "joined" ? "joined" : "sent");
      if (data.status === "joined") setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      setErr((e as Error).message);
      setStatus("error");
    }
  };

  return (
    <Centered>
      <h1 style={{ fontSize: "1.3rem" }}>No CoreFinOps workspace yet</h1>
      {apiBaseUrl && tokenSource && status === "idle" && !orgTenantId ? (
        <StartWorkspace apiBaseUrl={apiBaseUrl} tokenSource={tokenSource} onWorkspace={onWorkspace} />
      ) : null}
      {status === "sent" ? (
        <p role="status" style={{ color: "#1a7a3a", background: "#eafbef", padding: "0.6rem 0.8rem", borderRadius: 6 }}>
          Request sent. An administrator of that workspace will review it and grant you access.
        </p>
      ) : status === "joined" ? (
        <p role="status" style={{ color: "#1a7a3a", background: "#eafbef", padding: "0.6rem 0.8rem", borderRadius: 6 }}>
          You&apos;re in. Taking you to your workspace…
        </p>
      ) : status === "already" ? (
        <p role="status">Good news — you already have access. Try reloading the page.</p>
      ) : (
        <>
          <h2 style={{ fontSize: "1rem", marginTop: 24 }}>Joining your company&apos;s workspace?</h2>
          {adminApiBaseUrl ? (
            <>
              <p style={{ color: "#555", fontSize: "0.9rem" }}>
                {orgTenantId
                  ? "Request access to this workspace and an administrator will review it."
                  : "Enter the invitation code your administrator emailed you."}
              </p>
              {!orgTenantId ? (
                <input
                  value={inviteToken}
                  onChange={(e) => setInviteToken(e.target.value)}
                  placeholder="Invitation code"
                  autoComplete="one-time-code"
                  style={{ width: "100%", boxSizing: "border-box", padding: "0.5rem", borderRadius: 6, border: "1px solid #ccc", marginBottom: 8, fontFamily: "inherit" }}
                />
              ) : null}
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Optional note to the admin (e.g. which team you're on)"
                rows={2}
                style={{ width: "100%", boxSizing: "border-box", padding: "0.5rem", borderRadius: 6, border: "1px solid #ccc", marginBottom: 8, fontFamily: "inherit" }}
              />
              {err ? <p role="alert" style={{ color: "#a11" }}>{err}</p> : null}
              <button
                type="button"
                onClick={() => void request()}
                disabled={status === "sending"}
                style={{ padding: "0.6rem 1.2rem", fontSize: "1rem", background: "#1d6fb8", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
              >
                {status === "sending" ? "Sending…" : orgTenantId ? "Request access" : "Join workspace"}
              </button>
            </>
          ) : (
            <p>Ask your CoreFinOps administrator to add you.</p>
          )}
        </>
      )}
      <p style={{ color: "#777", fontSize: "0.8rem", marginTop: 16 }}>User id: <code>{sub}</code></p>
      <button type="button" onClick={onSignOut} style={{ marginTop: 4 }}>Sign out</button>
    </Centered>
  );
}

// ---------------------------------------------------------------------------
// WEB-01 / WEB-02: start a workspace (or a demo) without anyone's approval
// ---------------------------------------------------------------------------

async function postMe<T>(apiBaseUrl: string, tokenSource: TokenSource, path: string, body: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
  const bearer = await tokenSource.currentToken();
  if (!bearer) throw new Error("Your session has expired. Please sign in again.");
  const res = await fetch(`${apiBaseUrl.replace(/\/$/, "")}${path}`, {
    method: "POST",
    headers: { authorization: `Bearer ${bearer}`, "content-type": "application/json", accept: "application/json", ...extraHeaders },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: { message?: string } };
  if (!res.ok) throw new Error(data.error?.message ?? `Request failed (${res.status}).`);
  return data;
}

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AUD", "CAD", "JPY", "SGD"];

function StartWorkspace({ apiBaseUrl, tokenSource, onWorkspace }: { apiBaseUrl: string; tokenSource: TokenSource; onWorkspace: (tenantId: string) => void }): ReactNode {
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [busy, setBusy] = useState<"create" | "demo" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // One Idempotency-Key per (name, currency): a double click or a retry after a
  // network error returns the SAME workspace; editing the form starts a new request.
  const keyRef = useRef<{ fp: string; key: string } | null>(null);
  const idempotencyKey = (): string => {
    const fp = `${name.trim()}|${currency}`;
    if (keyRef.current?.fp !== fp) keyRef.current = { fp, key: `ws-${crypto.randomUUID()}` };
    return keyRef.current.key;
  };
  const run = async (kind: "create" | "demo") => {
    setBusy(kind);
    setErr(null);
    try {
      const r =
        kind === "create"
          ? await postMe<{ tenantId: string }>(apiBaseUrl, tokenSource, "/me/workspaces", { name: name.trim(), currency }, { "idempotency-key": idempotencyKey() })
          : await postMe<{ tenantId: string }>(apiBaseUrl, tokenSource, "/me/demo-workspace", {});
      onWorkspace(r.tenantId);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(null);
    }
  };
  const field = { padding: "0.5rem", borderRadius: 6, border: "1px solid #ccc", fontFamily: "inherit", fontSize: "0.95rem" } as const;
  return (
    <section aria-label="Start a workspace">
      <p style={{ color: "#555" }}>Start your own workspace — a 14-day free trial, no card required — or look around a demo with sample data first.</p>
      <label style={{ display: "block", fontSize: "0.85rem", color: "#555" }} htmlFor="ws-name">Company or team name</label>
      <div style={{ display: "flex", gap: 8, margin: "4px 0 10px", flexWrap: "wrap" }}>
        <input id="ws-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Retail" style={{ ...field, flex: "1 1 200px" }} />
        <label className="cfo-sr-only" htmlFor="ws-currency">Billing currency</label>
        <select id="ws-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} style={field}>
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => void run("create")}
          disabled={busy !== null || name.trim().length < 2}
          style={{ padding: "0.6rem 1.2rem", fontSize: "1rem", background: "#1d6fb8", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
        >
          {busy === "create" ? "Creating…" : "Create workspace"}
        </button>
        <button type="button" onClick={() => void run("demo")} disabled={busy !== null} style={{ padding: "0.6rem 1.2rem", fontSize: "1rem", borderRadius: 6, cursor: "pointer" }}>
          {busy === "demo" ? "Preparing demo…" : "Explore a demo"}
        </button>
      </div>
      {err ? <p role="alert" style={{ color: "#a11" }}>{err}</p> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// MP-02 / MP-05: link an AWS Marketplace purchase to a workspace
// ---------------------------------------------------------------------------

function MarketplaceLink({
  handle,
  tenants,
  apiBaseUrl,
  tokenSource,
  onSignOut,
  onDone,
}: {
  handle: string;
  tenants: Tenant[];
  apiBaseUrl: string;
  tokenSource: TokenSource | null;
  onSignOut: () => void;
  onDone: (tenantId: string | null) => void;
}): ReactNode {
  const adminOf = tenants.filter((t) => t.roles.includes("org_admin"));
  const [preview, setPreview] = useState<{ status: string; planCode: string | null; licenseConfirmed?: boolean } | null>(null);
  const [mode, setMode] = useState<"new" | "existing">(adminOf.length ? "existing" : "new");
  const [tenantId, setTenantId] = useState(adminOf[0]?.tenantId ?? "");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!tokenSource) return;
    postMe<{ status: string; planCode: string | null; licenseConfirmed?: boolean }>(apiBaseUrl, tokenSource, "/me/marketplace/registration", { handle })
      .then(setPreview)
      .catch((e: Error) => setErr(e.message));
  }, [apiBaseUrl, tokenSource, handle]);

  const link = async () => {
    if (!tokenSource) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await postMe<{ tenantId: string; status: string }>(apiBaseUrl, tokenSource, "/me/marketplace/link", mode === "existing" ? { handle, tenantId } : { handle, workspaceName: name.trim() });
      onDone(r.tenantId);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  const unusable = preview && preview.status !== "pending";
  return (
    <Centered>
      <h1 style={{ fontSize: "1.3rem" }}>Activate your AWS Marketplace subscription</h1>
      {!preview && !err ? <p role="status">Checking your subscription…</p> : null}
      {preview && !unusable ? (
        <>
        <p style={{ color: "#555", fontSize: "0.9rem" }}>
          The AWS account that bought this subscription does not need to be one of the AWS accounts you analyse. You connect those separately, with read-only access, after activation.
        </p>
        <p style={{ color: "#555" }}>
          {preview.planCode
            ? <>Your <strong>{preview.planCode}</strong> subscription {preview.licenseConfirmed ? "is ready" : "was received and AWS is still confirming the licence"}. Choose the workspace it should apply to. {preview.licenseConfirmed ? "" : "It activates automatically as soon as AWS confirms, usually within a few minutes. "}Billing stays on your AWS invoice.</>
            : <>AWS is still confirming your subscription. Link a workspace now; it activates automatically as soon as AWS confirms the licence, usually within a few minutes.</>}
        </p>
        </>
      ) : null}
      {unusable ? (
        <p role="alert" style={{ color: "#8a5a00" }}>{preview?.status === "linked" ? "This subscription is already linked to a workspace." : "This sign-up link has expired. Return to AWS Marketplace and choose \"Set up your account\" again."}</p>
      ) : preview ? (
        <fieldset style={{ border: "none", padding: 0 }}>
          <legend className="cfo-sr-only">Workspace</legend>
          {adminOf.length ? (
            <label style={{ display: "block", marginBottom: 8 }}>
              <input type="radio" checked={mode === "existing"} onChange={() => setMode("existing")} /> Use a workspace I administer{" "}
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={mode !== "existing"}>
                {adminOf.map((t) => <option key={t.tenantId} value={t.tenantId}>{t.name}</option>)}
              </select>
            </label>
          ) : null}
          <label style={{ display: "block", marginBottom: 12 }}>
            <input type="radio" checked={mode === "new"} onChange={() => setMode("new")} /> Create a new workspace{" "}
            <input value={name} onChange={(e) => setName(e.target.value)} disabled={mode !== "new"} placeholder="Company name" aria-label="New workspace name" />
          </label>
          <button
            type="button"
            onClick={() => void link()}
            disabled={busy || (mode === "new" ? name.trim().length < 2 : !tenantId)}
            style={{ padding: "0.6rem 1.2rem", fontSize: "1rem", background: "#1d6fb8", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
          >
            {busy ? "Activating…" : "Activate subscription"}
          </button>
        </fieldset>
      ) : null}
      {err ? <p role="alert" style={{ color: "#a11" }}>{err}</p> : null}
      <p style={{ marginTop: 20 }}>
        <button type="button" onClick={() => onDone(null)}>Skip for now</button>{" "}
        <button type="button" onClick={onSignOut}>Sign out</button>
      </p>
    </Centered>
  );
}
